/**
 * Service Pricing Breakdown Component
 * Shows detailed pricing: labor (no tax), parts (16.8% markup + 7% tax), 
 * roadside (7% full tax), tips, donations
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  DollarSign, Info, ChevronDown, ChevronUp, Wrench, 
  Package, Calculator, Receipt
} from 'lucide-react';

// Pricing constants matching backend
const LABOR_PERCENT = 0.78;  // 78% of mechanic services is labor
const PARTS_MARKUP = 0.168;  // 16.8% markup on parts
const SALES_TAX = 0.07;      // 7% sales tax

// Service type classification
const ROADSIDE_SERVICES = ['towing', 'tow', 'jump_start', 'flat_tire', 'lockout', 'fuel_delivery', 'battery_jump'];
const MECHANIC_SERVICES = ['repair', 'diagnostic', 'oil_change', 'brake', 'engine', 'transmission', 'electrical'];

export default function PricingBreakdown({ 
  serviceType = 'repair',
  basePrice = 0,
  partsCost = 0,
  tipAmount = 0,
  donationAmount = 0,
  showRevenueSplit = false,
  compact = false
}) {
  const [showDetails, setShowDetails] = useState(!compact);
  
  // Determine service category
  const isRoadside = ROADSIDE_SERVICES.some(s => serviceType.toLowerCase().includes(s));
  const isMechanic = !isRoadside;

  // Calculate parts pricing (mechanic services only)
  const partsMarkup = isMechanic && partsCost > 0 ? Math.round(partsCost * PARTS_MARKUP * 100) / 100 : 0;
  const partsSubtotal = partsCost + partsMarkup;
  const partsTax = isMechanic && partsCost > 0 ? Math.round(partsSubtotal * SALES_TAX * 100) / 100 : 0;
  const partsTotal = partsSubtotal + partsTax;

  // Calculate labor (mechanic services: 78% is labor, no tax)
  const laborCost = isMechanic ? Math.round(basePrice * LABOR_PERCENT * 100) / 100 : 0;
  const nonLaborCost = isMechanic ? Math.round(basePrice * (1 - LABOR_PERCENT) * 100) / 100 : basePrice;
  
  // Calculate service tax
  const serviceTax = isMechanic 
    ? Math.round(nonLaborCost * SALES_TAX * 100) / 100  // Only tax non-labor portion
    : Math.round(basePrice * SALES_TAX * 100) / 100;    // Roadside: full tax

  // Totals
  const totalTax = serviceTax + partsTax;
  const subtotalWithTax = basePrice + serviceTax + partsTotal;
  const grandTotal = subtotalWithTax + tipAmount + donationAmount;

  // Revenue split (for provider/admin views)
  const revenueBase = basePrice + partsSubtotal;
  const providerPay = Math.round(revenueBase * 0.30 * 100) / 100 + tipAmount;
  const ownerPay = Math.round(revenueBase * 0.40 * 100) / 100;
  const adminPay = Math.round(revenueBase * 0.08 * 100) / 100;
  const operationsCost = Math.round(revenueBase * 0.22 * 100) / 100;

  if (compact) {
    return (
      <div className="text-sm space-y-1" data-testid="pricing-breakdown-compact">
        <div className="flex justify-between">
          <span>Service</span>
          <span>${basePrice.toFixed(2)}</span>
        </div>
        {isMechanic && laborCost > 0 && (
          <div className="flex justify-between text-gray-500 text-xs pl-2">
            <span>└ Labor (no tax)</span>
            <span>${laborCost.toFixed(2)}</span>
          </div>
        )}
        {partsCost > 0 && (
          <>
            <div className="flex justify-between">
              <span>Parts</span>
              <span>${partsTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-500 text-xs pl-2">
              <span>└ Markup (16.8%)</span>
              <span>+${partsMarkup.toFixed(2)}</span>
            </div>
          </>
        )}
        <div className="flex justify-between text-gray-500">
          <span>Tax</span>
          <span>${totalTax.toFixed(2)}</span>
        </div>
        {tipAmount > 0 && (
          <div className="flex justify-between text-pink-600">
            <span>Tip</span>
            <span>+${tipAmount.toFixed(2)}</span>
          </div>
        )}
        {donationAmount > 0 && (
          <div className="flex justify-between text-purple-600">
            <span>Donation</span>
            <span>+${donationAmount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold pt-1 border-t">
          <span>Total</span>
          <span>${grandTotal.toFixed(2)}</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="w-full" data-testid="pricing-breakdown">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Price Breakdown
          </CardTitle>
          <Badge variant={isRoadside ? "secondary" : "default"}>
            {isRoadside ? 'Roadside Service' : 'Mechanic Service'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Service Charge */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-blue-600" />
              <span className="font-medium">Service</span>
            </div>
            <span className="font-semibold">${basePrice.toFixed(2)}</span>
          </div>
          
          {/* Labor breakdown for mechanic services */}
          {isMechanic && (
            <div className="ml-6 text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span className="flex items-center gap-1">
                  Labor (78%)
                  <Badge variant="outline" className="text-xs ml-1">No Tax</Badge>
                </span>
                <span>${laborCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Non-labor (22%)</span>
                <span>${nonLaborCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Tax on non-labor (7%)</span>
                <span>+${serviceTax.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Roadside service tax */}
          {isRoadside && (
            <div className="ml-6 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Sales Tax (7%)</span>
                <span>+${serviceTax.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Parts (if any) */}
        {partsCost > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-purple-600" />
                <span className="font-medium">Parts</span>
              </div>
              <span className="font-semibold">${partsTotal.toFixed(2)}</span>
            </div>
            
            <div className="ml-6 text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Base cost</span>
                <span>${partsCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Markup (16.8%)</span>
                <span>+${partsMarkup.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Parts subtotal</span>
                <span>${partsSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Tax (7%)</span>
                <span>+${partsTax.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Subtotal */}
        <div className="flex justify-between pt-2 border-t">
          <span>Subtotal (with tax)</span>
          <span className="font-semibold">${subtotalWithTax.toFixed(2)}</span>
        </div>

        {/* Tip */}
        {tipAmount > 0 && (
          <div className="flex justify-between text-pink-600">
            <span>Tip (100% to provider)</span>
            <span>+${tipAmount.toFixed(2)}</span>
          </div>
        )}

        {/* Donation */}
        {donationAmount > 0 && (
          <div className="flex justify-between text-purple-600">
            <span>Round-up Donation</span>
            <span>+${donationAmount.toFixed(2)}</span>
          </div>
        )}

        {/* Grand Total */}
        <div className="flex justify-between pt-3 border-t-2 text-lg">
          <span className="font-bold">Total</span>
          <span className="font-bold text-green-600">${grandTotal.toFixed(2)}</span>
        </div>

        {/* Tax Summary */}
        <div className="p-3 bg-gray-50 rounded-lg text-sm">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-4 h-4 text-gray-600" />
            <span className="font-medium">Tax Summary</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-gray-600">
            <div>Service Tax:</div>
            <div className="text-right">${serviceTax.toFixed(2)}</div>
            {partsCost > 0 && (
              <>
                <div>Parts Tax:</div>
                <div className="text-right">${partsTax.toFixed(2)}</div>
              </>
            )}
            <div className="font-medium">Total Tax:</div>
            <div className="text-right font-medium">${totalTax.toFixed(2)}</div>
          </div>
        </div>

        {/* Revenue Split (Admin/Provider view) */}
        {showRevenueSplit && (
          <div className="pt-3 border-t">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Revenue Split
            </button>
            
            {showDetails && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Provider (30%{tipAmount > 0 ? ' + tip' : ''})</span>
                  <span className="font-medium text-green-600">${providerPay.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Owner (40%)</span>
                  <span className="font-medium">${ownerPay.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Admin (8% max)</span>
                  <span className="font-medium">${adminPay.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Operations (22%)</span>
                  <span className="font-medium">${operationsCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t text-gray-500">
                  <span>Tax Collected</span>
                  <span>${totalTax.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info Note */}
        <div className="flex items-start gap-2 text-xs text-gray-500 pt-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            {isMechanic 
              ? 'Labor charges (78% of service) are not taxed. Parts include 16.8% markup plus 7% tax.'
              : 'Roadside services include 7% sales tax on the full amount.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
